import {
  HttpError,
  IResourceComponentsProps,
  useApiUrl,
  useNavigation,
  useNotification,
  usePermissions,
} from "@pankod/refine-core";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Icons,
  List,
  Modal,
  Progress,
  Row,
  Select,
  Space,
  ShowButton,
  Table,
  Tag,
  Tooltip,
  Typography,
  getDefaultSortOrder,
  useSelect,
  useTable,
} from "@pankod/refine-antd";
import dayjs, { Dayjs } from "dayjs";
import { useState } from "react";
import { ConsumerLogs, IHealthInsurer } from "interfaces";
import {
  ORIGIN_OPTIONS,
  PROCESS_STATUS_OPTIONS,
  TONE_COLOR,
  UserRole,
  originLabel,
  processStatus,
} from "enums";
import { dateFormater } from "utils/date-formater";
import { formatDuration } from "utils/duration";
import { axiosInstance } from "services/axios";
import {
  ProcessSignals,
  findRunningProcess,
  metricNumber,
  processSignals,
  processTone,
} from "./aggregations";

const { Text } = Typography;

interface SearchValues {
  status?: string;
  command_origin?: string;
  execution_date?: Dayjs;
}

interface DispatchError {
  stage?: string;
  message?: string;
}

export const PlanReloadList: React.FC<IResourceComponentsProps> = () => {
  const permissions = usePermissions();
  const isAdmin = permissions.data === UserRole.Admin;
  const basicAccess = permissions.data === UserRole.ReportsBasic;

  const apiUrl = useApiUrl();
  const { show } = useNavigation();
  const toast = useNotification();
  const [operatorId, setOperatorId] = useState<number | null>(null);
  const [operatorLabel, setOperatorLabel] = useState<string>("");
  const [reprocessing, setReprocessing] = useState(false);
  const [dispatchError, setDispatchError] = useState<DispatchError | null>(null);

  const { selectProps } = useSelect<IHealthInsurer>({
    resource: "v1/health-insurer",
    optionLabel: "Description",
    optionValue: "HealthInsurerId",
    hasPagination: true,
  });

  const { tableProps, searchFormProps, sorter, tableQueryResult, filters } = useTable<
    ConsumerLogs,
    HttpError,
    SearchValues
  >({
    onSearch: (values) => [
      { field: "status", operator: "eq", value: values.status },
      { field: "command_origin", operator: "eq", value: values.command_origin },
      {
        field: "execution_date",
        operator: "eq",
        // O backend espera `YYYY-MM-DD` (`date` do FastAPI); o DatePicker devolve dayjs.
        value: values.execution_date
          ? dayjs(values.execution_date).format("YYYY-MM-DD")
          : undefined,
      },
    ],
    initialCurrent: 1,
    initialPageSize: 10,
    hasPagination: true,
  });

  const records = tableQueryResult.data?.data ?? [];
  const running = findRunningProcess(records);
  // Derivado dos filtros aplicados (e não de `form.getFieldsValue()` lido no render): o form pode
  // não refletir filtros restaurados numa remontagem, e o estado vazio sairia com o texto errado
  // (R08 do review).
  const hasFilters = (filters ?? []).some(
    (filter) => "value" in filter && Boolean(filter.value)
  );

  async function reprocess() {
    const url =
      operatorId != null
        ? `${apiUrl}/v1/consumers/plans-by-hi/${operatorId}`
        : `${apiUrl}/v1/consumers/plans`;
    setReprocessing(true);
    setDispatchError(null);
    try {
      const response = await axiosInstance.get(url);
      const processId: string | undefined = response.data?.process_id;
      toast.open?.({
        message: "Recarga iniciada",
        description: processId
          ? `Processo ${processId} em andamento.`
          : "Acompanhe o processo na lista.",
        type: "success",
      });
      // O disparo retorna o process_id; o Show busca por _id — localizamos o _id na lista
      // recém-atualizada (o processo novo é o mais recente) e abrimos o dashboard (CA04 da 027).
      const refetched = await tableQueryResult.refetch();
      const created = refetched.data?.data?.find(
        (row) => row.process_id === processId
      );
      if (created?._id) show("v1/consumer-log", created._id);
    } catch (error: any) {
      // O backend (spec 028) retorna { stage, message } na falha do disparo. O toast desaparece
      // antes de o operador agir, então a etapa também fica no card, até ser fechada.
      const body = error?.response?.data;
      const failure: DispatchError = {
        stage: body?.stage,
        message: body?.message,
      };
      setDispatchError(failure);
      toast.open?.({
        message: "Erro",
        description: failure.stage
          ? `Falha no disparo — etapa: ${failure.stage}. ${failure.message ?? ""}`.trim()
          : "Não foi possível iniciar a recarga. Tente novamente.",
        type: "error",
      });
    } finally {
      setReprocessing(false);
    }
  }

  function confirmReprocess() {
    const isFull = operatorId == null;
    Modal.confirm({
      title: isFull
        ? "Reprocessar todas as operadoras"
        : `Reprocessar ${operatorLabel || "a operadora selecionada"}`,
      content: isFull
        ? "A recarga busca os planos no SIGO e substitui a prateleira de cada filial, uma " +
          "transação por filial. Filial sem planos válidos no SIGO fica com a prateleira " +
          "zerada até a próxima recarga com sucesso. Esta carga é pesada e pode levar " +
          "alguns minutos."
        : "A recarga busca os planos no SIGO e substitui a prateleira de cada filial da " +
          "operadora, uma transação por filial. Filial sem planos válidos no SIGO fica com " +
          "a prateleira zerada até a próxima recarga com sucesso.",
      okText: "Reprocessar",
      cancelText: "Cancelar",
      onOk: reprocess,
    });
  }

  function handleChangeOperator(value: unknown, option: any) {
    setOperatorId(typeof value === "number" ? value : null);
    setOperatorLabel(typeof value === "number" ? option?.label ?? "" : "");
  }

  function handleClear() {
    searchFormProps.form?.resetFields();
    searchFormProps.form?.submit();
  }

  return (
    <List title="Recarga de Planos">
      {running && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Recarga em andamento"
          description={
            <RunningDescription record={running} />
          }
          action={
            <Button
              size="small"
              icon={<Icons.EyeOutlined />}
              disabled={basicAccess}
              onClick={() => running._id && show("v1/consumer-log", running._id)}
            >
              Acompanhar
            </Button>
          }
        />
      )}

      {/* Disparo restrito a Admin (RN06 da 027): sem permissão o bloco não é renderizado. */}
      {isAdmin && (
        <Card title="Disparar Recarga" style={{ marginBottom: 16 }}>
          {dispatchError && (
            <Alert
              type="error"
              showIcon
              closable
              style={{ marginBottom: 16 }}
              onClose={() => setDispatchError(null)}
              message={
                dispatchError.stage
                  ? `Falha no disparo — etapa: ${dispatchError.stage}`
                  : "Falha no disparo"
              }
              description={
                dispatchError.message ??
                "Não foi possível iniciar a recarga. Tente novamente."
              }
            />
          )}

          <Form layout="vertical">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item label="Operadora" style={{ marginBottom: 0 }}>
                  <Select
                    {...selectProps}
                    defaultActiveFirstOption
                    options={[
                      { label: "Todas as operadoras", value: "" },
                      ...(selectProps.options ?? []),
                    ]}
                    onChange={handleChangeOperator}
                    placeholder="Selecione a operadora"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={16}>
                <Form.Item label="Abrangência" style={{ marginBottom: 0 }}>
                  <Text type="secondary">
                    {operatorId != null
                      ? "Todas as filiais ativas da operadora selecionada."
                      : "Todas as filiais ativas de todas as operadoras."}
                    {" A recarga substitui os planos filial por filial, em transação: filial " +
                      "sem planos válidos no SIGO fica com a prateleira zerada."}
                  </Text>
                </Form.Item>
              </Col>
            </Row>
            <Row justify="end" style={{ marginTop: 16 }}>
              <Button
                type="primary"
                loading={reprocessing}
                icon={<Icons.ReloadOutlined />}
                onClick={confirmReprocess}
              >
                Reprocessar
              </Button>
            </Row>
          </Form>
        </Card>
      )}

      <Card title="Filtrar" style={{ marginBottom: 16 }}>
        <Form {...searchFormProps} layout="vertical">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item name="status" label="Status" style={{ marginBottom: 0 }}>
                <Select
                  options={PROCESS_STATUS_OPTIONS}
                  placeholder="Selecione o status"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item
                name="command_origin"
                label="Origem"
                style={{ marginBottom: 0 }}
              >
                <Select options={ORIGIN_OPTIONS} placeholder="Selecione a origem" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item
                name="execution_date"
                label="Data de Execução"
                style={{ marginBottom: 0 }}
                rules={[
                  {
                    validator: (_, value) =>
                      value && dayjs(value).isAfter(dayjs().endOf("day"))
                        ? Promise.reject(
                            "A data de execução não pode ser futura."
                          )
                        : Promise.resolve(),
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder="Data de Execução"
                  disabledDate={(current) =>
                    !!current && current > dayjs().endOf("day")
                  }
                />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="end" style={{ marginTop: 16 }}>
            <Space>
              <Button icon={<Icons.ClearOutlined />} onClick={handleClear}>
                Limpar
              </Button>
              <Button
                type="primary"
                icon={<Icons.SearchOutlined />}
                htmlType="submit"
              >
                Buscar
              </Button>
            </Space>
          </Row>
        </Form>
      </Card>

      <Table
        {...tableProps}
        rowKey="_id"
        loading={tableQueryResult.isLoading}
        scroll={{ x: 1100 }}
        pagination={{
          ...tableProps.pagination,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} de ${total} registros`,
        }}
        locale={{
          emptyText: hasFilters
            ? "Nenhuma recarga encontrada para os filtros aplicados."
            : "Nenhuma recarga registrada. Dispare uma recarga para começar.",
        }}
      >
        <Table.Column<ConsumerLogs>
          dataIndex="process_id"
          key="process_id"
          title="Processo"
          defaultSortOrder={getDefaultSortOrder("process_id", sorter)}
          sorter
          render={(value, record) => (
            <div>
              <Text className="plan-reload-mono" copyable={{ text: value }}>
                {value ?? "-"}
              </Text>
              <div style={{ marginTop: 4 }}>
                <Tag>{originLabel(record.command_origin)}</Tag>
              </div>
            </div>
          )}
        />
        <Table.Column<ConsumerLogs>
          dataIndex="is_full_process"
          key="is_full_process"
          title="Abrangência"
          render={(value) =>
            value ? "Todas as operadoras" : "Por operadora"
          }
        />
        <Table.Column
          dataIndex="start_time"
          key="start_time"
          title="Início"
          defaultSortOrder={getDefaultSortOrder("start_time", sorter)}
          sorter
          render={(value) => dateFormater(value) ?? "-"}
        />
        <Table.Column<ConsumerLogs>
          title="Duração"
          dataIndex="end_time"
          render={(_, record) => {
            const done = record.last_status === "finalizado";
            return (
              <div>
                {formatDuration(
                  record.start_time,
                  done ? record.end_time : undefined
                )}
                {!done && (
                  <Text
                    type="secondary"
                    style={{ display: "block", fontSize: 11 }}
                  >
                    em curso
                  </Text>
                )}
              </div>
            );
          }}
        />
        <Table.Column<ConsumerLogs>
          title="Filiais"
          dataIndex="statistics"
          render={(_, record) => <BranchProgress record={record} />}
        />
        <Table.Column<ConsumerLogs>
          title="Planos"
          dataIndex="plans"
          align="right"
          render={(_, record) => {
            const signals = processSignals(record);
            return metricNumber(signals.totalPlans, signals.reportsPlans);
          }}
        />
        <Table.Column<ConsumerLogs>
          title="Atenção"
          dataIndex="attention"
          render={(_, record) => <AttentionTags signals={processSignals(record)} />}
        />
        <Table.Column<ConsumerLogs>
          dataIndex="last_status"
          key="last_status"
          title="Status"
          defaultSortOrder={getDefaultSortOrder("last_status", sorter)}
          sorter
          render={(value, record) => {
            const { noPlans } = processSignals(record);
            const status = processStatus(value, noPlans);
            return <Tag color={status.color}>{status.label}</Tag>;
          }}
        />
        <Table.Column<ConsumerLogs>
          title="Ações"
          dataIndex="actions"
          render={(_, record) => (
            <Space>
              <Tooltip title="Acompanhar">
                <ShowButton
                  hidden={basicAccess}
                  hideText
                  size="small"
                  recordItemId={record._id}
                />
              </Tooltip>
            </Space>
          )}
        />
      </Table>
    </List>
  );
};

const RunningDescription: React.FC<{ record: ConsumerLogs }> = ({ record }) => {
  const signals = processSignals(record);

  return (
    <span>
      {"Processo "}
      <Text className="plan-reload-mono">{record.process_id}</Text>
      {record.start_time ? ` iniciado em ${dateFormater(record.start_time)}` : ""}
      {signals.total
        ? ` — ${signals.finalized} de ${signals.total} filiais concluídas.`
        : "."}
    </span>
  );
};

const BranchProgress: React.FC<{ record: ConsumerLogs }> = ({ record }) => {
  const signals = processSignals(record);

  if (!signals.total) return <Text type="secondary">-</Text>;

  const tone = processTone({
    failures: signals.failures,
    noPlans: signals.noPlans,
    done: record.last_status === "finalizado",
  });

  return (
    <Space size={8}>
      <Progress
        percent={signals.progress}
        strokeColor={TONE_COLOR[tone]}
        trailColor="var(--ant-panel-border)"
        showInfo={false}
        style={{ width: 72, marginBottom: 0 }}
      />
      <Text>{`${signals.finalized}/${signals.total}`}</Text>
    </Space>
  );
};

/**
 * Sinais de atenção do processo direto na linha — falha (vermelho) e "sem planos"/"sem família"
 * (âmbar) são semânticas DISTINTAS (RN11 da 033, RN02 da 035) e nunca se somam num só número.
 */
export const AttentionTags: React.FC<{ signals: ProcessSignals }> = ({
  signals,
}) => {
  const tags: React.ReactNode[] = [];

  if (signals.failures > 0) {
    tags.push(
      <Tag key="fail" color="error">
        {signals.failures === 1 ? "1 falha" : `${signals.failures} falhas`}
      </Tag>
    );
  }
  if (signals.emptyBranches > 0) {
    tags.push(
      <Tag key="empty" color="warning">
        {`${signals.emptyBranches} sem planos`}
      </Tag>
    );
  }
  if (signals.withoutFamilyPlans > 0) {
    tags.push(
      // "planos" explícito: no detalhe o recorte com o mesmo nome conta FILIAIS, e dois números
      // diferentes sob o mesmo rótulo em telas vizinhas confundem (R06 do review).
      <Tag key="nofamily" color="warning">
        {signals.withoutFamilyPlans === 1
          ? "1 plano sem família"
          : `${signals.withoutFamilyPlans} planos sem família`}
      </Tag>
    );
  }

  if (!tags.length) return <Text type="secondary">-</Text>;

  return <Space size={4} wrap>{tags}</Space>;
};
